/**
 * Structured estimate display. Constitution III — ranges only,
 * never single-point. Constitution II — every dollar traces back to
 * a catalog row + ZIP factor + measurement.
 */

import type { EstimateSnapshot, LineItem } from "@starz/shared/api/types.gen";

interface Props {
  payload: {
    kind: "snapshot" | "missing_inputs" | "error";
    snapshot?: EstimateSnapshot;
    needed?: string[];
    message?: string;
  };
  /** Optional CTA when `needed` includes "selections" — posts a chat
   * turn that nudges Stella to emit `[[OPTIONS:…]]` for the right
   * option group. Without this the customer is left guessing what to
   * type next. */
  onAskForOptions?: () => void;
}

function formatRange(low: number, high: number): string {
  const fmt = (cents: number) =>
    (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  // Collapse to a single price when low and high round to the same
  // dollar (common when ZIP factor is 1.0 and the source measurement
  // has a tight range like customer-typed dimensions).
  if (Math.round(low / 100) === Math.round(high / 100)) {
    return fmt(low);
  }
  return `${fmt(low)} – ${fmt(high)}`;
}

function _formatQty(qty: number): string {
  if (Number.isInteger(qty)) return qty.toString();
  return (Math.round(qty * 10) / 10).toFixed(1);
}

function _shortUnit(unit: string): string {
  switch (unit) {
    case "sqft":
      return "sq ft";
    case "linear_ft":
      return "linear ft";
    case "gallon":
      return "gal";
    case "each":
      return "each";
    default:
      return unit;
  }
}

function _pluralUnit(unit: string, qty: number | null): string {
  switch (unit) {
    case "sqft":
      return "sq ft";
    case "linear_ft":
      return "linear ft";
    case "gallon":
      return qty !== null && Math.abs(qty - 1) < 0.05 ? "gallon" : "gallons";
    case "each":
      return "";
    default:
      return unit;
  }
}

function _qtyLabel(li: LineItem): string {
  if (li.qty_low === null || li.qty_high === null) return _pluralUnit(li.unit, null);
  const unitLabel = _pluralUnit(li.unit, li.qty_low);
  if (li.qty_low === li.qty_high) {
    return `${_formatQty(li.qty_low)} ${unitLabel}`.trim();
  }
  return `${_formatQty(li.qty_low)} – ${_formatQty(li.qty_high)} ${unitLabel}`.trim();
}

function _ratePerUnit(li: LineItem): string {
  return `$${(li.unit_price_cents / 100).toFixed(2)}/${_shortUnit(li.unit)}`;
}

export function EstimateSnapshotPanel(props: Props) {
  if (props.payload.kind === "missing_inputs") {
    const needed = props.payload.needed ?? [];
    const needsSelections = needed.includes("selections");
    return (
      <section
        aria-label="Estimate not yet ready"
        className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm"
      >
        <h3 className="font-serif text-xl text-starz-navy">Almost there…</h3>
        <p className="mt-2 text-starz-charcoal">
          {needsSelections
            ? "I haven't picked any materials with you yet. Want to see what your contractor offers?"
            : (props.payload.message ?? "Some inputs are still needed.")}
        </p>
        {!needsSelections && needed.length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-warm-gray">
            {needed.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
        {needsSelections && props.onAskForOptions && (
          <button
            type="button"
            onClick={props.onAskForOptions}
            className="mt-4 min-h-[44px] rounded-lg bg-starz-navy px-5 py-2 text-sm font-semibold uppercase tracking-wide text-starz-off-white hover:bg-starz-navy/90"
          >
            Show me material options
          </button>
        )}
      </section>
    );
  }

  if (props.payload.kind === "error") {
    return (
      <section
        aria-label="Estimate failed"
        className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700"
      >
        {props.payload.message ?? "An error occurred while computing the estimate."}
      </section>
    );
  }

  if (!props.payload.snapshot) return null;
  const s = props.payload.snapshot;
  return (
    <section
      aria-label="Preliminary estimate"
      className="space-y-4 rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
    >
      <header>
        <h3 className="font-serif text-xl text-starz-navy">Preliminary estimate</h3>
        <p className="mt-1 text-xs text-warm-gray">
          Ranges, not single numbers — preliminary and non-binding until your contractor reviews.
          ZIP factor used:{" "}
          <span className="font-mono text-starz-charcoal">{s.zip_factor_used.toFixed(3)}</span>
          {s.zip_used ? ` (${s.zip_used})` : " (default)"}.
        </p>
      </header>
      <ul className="divide-y divide-starz-gold/10">
        {s.line_items.map((li) => (
          <li key={li.line_id} className="py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-base font-medium text-starz-navy">{li.display_name}</span>
              <span className="font-mono text-base text-starz-charcoal">
                {formatRange(li.line_low_cents, li.line_high_cents)}
              </span>
            </div>
            <div className="mt-1 text-xs text-warm-gray">
              {_qtyLabel(li)} × {_ratePerUnit(li)}
              {li.zip_factor !== 1 ? ` × ${li.zip_factor.toFixed(3)} ZIP` : ""}
              {li.source === "admin_adjusted" ? " · adjusted by contractor" : ""}
            </div>
          </li>
        ))}
      </ul>
      <footer className="border-t border-starz-gold/15 pt-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-semibold uppercase tracking-widest text-starz-charcoal">
            Subtotal
          </span>
          <span className="font-mono text-lg text-starz-navy">
            {formatRange(s.subtotal_low_cents, s.subtotal_high_cents)}
          </span>
        </div>
        <p className="mt-3 text-xs italic text-warm-gray">
          Disclosure {s.non_binding_notice_version}: This estimate is preliminary. Materials, scope,
          and final pricing are subject to your contractor's review and approval.
        </p>
      </footer>
    </section>
  );
}
